import flatMap from "lodash/flatMap";
import {
  type StyledTextSpan,
  regexMatchAll,
  buildSpansWithMatchedRegex,
} from "@inbox/shared/common-styles";

const NORMAL_STYLE = "normal";

export class RichTextMessageHandler {
  private phases: IPhase[];
  constructor(phases: IPhase[]) {
    this.phases = phases;
  }
  public render(text: string): JSX.Element {
    const spans = this.parse(text);
    return this.getComponentFromSpans(spans);
  }
  parse(text: string) {
    try {
      let spans: StyledTextSpan[] = [{ style: NORMAL_STYLE, text }];
      for (const phase of this.phases) {
        spans = flatMap(spans, (span) => {
          if (span.style === NORMAL_STYLE) {
            return phase.run(span);
          }
          return [span];
        });
      }

      return spans;
    } catch (error) {
      console.warn(error);
    }
    return [{ style: NORMAL_STYLE, text }];
  }

  private getComponentFromSpans(spans: StyledTextSpan[]): JSX.Element {
    return (
      <span>
        {spans.map((c, idx) => {
          const foundPhase = this.phases.find((p) => p.style === c.style);
          if (foundPhase) {
            return foundPhase.render(c);
          }
          return <span key={idx}>{c.text}</span>;
        })}
      </span>
    );
  }
}

export const renderAccountNameBold = (
  message: string,
  accountName: string | null
) => {
  if (!accountName || !message) {
    return message;
  }
  const phase1 = new BoldOnIncludePhase(`@${accountName}`);
  const phase2 = new BoldOnIncludePhase(accountName);
  const richTextMessage = new RichTextMessageHandler([phase1, phase2]);
  return richTextMessage.render(message);
};

interface IPhase {
  style: string;
  run(span: StyledTextSpan): StyledTextSpan[];
  render(span: StyledTextSpan): JSX.Element;
}

// StyledTextSpan imported from @inbox/shared/common-styles

export class BoldOnIncludePhase implements IPhase {
  private name: string;
  constructor(name: string) {
    this.name = name;
  }
  style = "bold-on-include";
  run(span: StyledTextSpan): StyledTextSpan[] {
    const hashtagRegex = new RegExp(`(${escapeRegExp(this.name)})`, "g");
    const matches = regexMatchAll(span.text, hashtagRegex);
    if (matches.length <= 0) {
      return [span];
    }

    return buildSpansWithMatchedRegex(span.text, matches, (m) => ({
      style: this.style,
      text: `${m[1]}`,
    }));
  }
  render(span: StyledTextSpan): JSX.Element {
    return (
      <span>
        <b>{span.text}</b>
      </span>
    );
  }
}

// regexMatchAll and buildSpansWithMatchedRegex are now imported from @inbox/shared/common-styles

function escapeRegExp(text: string) {
  return text.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`); // $& means the whole matched string
}
